import type React from 'react'
import { useRef, useState } from 'react'
import { uploadUserMedia } from '../../api/entries'

export function markdownTag(filename: string, url: string, mimetype: string): string {
  if (mimetype.startsWith('image/')) return `![${filename}](${url})`
  return `[${filename}](${url})`
}

interface Options {
  username: string | null | undefined
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  content: string
  setContent: (v: string) => void
}

export function useMediaUpload({ username, textareaRef, content, setContent }: Options) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploadError, setUploadError] = useState('')

  function insertAtCursor(tag: string) {
    const el = textareaRef.current
    if (!el) {
      setContent(content + tag)
      return
    }
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = content.slice(0, start)
    const after = content.slice(end)
    const needsNewline = before.length > 0 && !before.endsWith('\n')
    const insertion = (needsNewline ? '\n' : '') + tag
    setContent(before + insertion + after)
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = start + insertion.length
      el.focus()
    })
  }

  async function uploadFile(file: File) {
    if (!username) return
    setUploadingCount((n) => n + 1)
    setUploadError('')
    try {
      const media = await uploadUserMedia(username, file)
      insertAtCursor(markdownTag(media.filename, media.url, media.mimetype))
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'upload failed')
    } finally {
      setUploadingCount((n) => n - 1)
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    for (const f of files) uploadFile(f)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const imageItems = Array.from(e.clipboardData.items).filter(
      (item) => item.type.startsWith('image/')
    )
    if (imageItems.length === 0) return
    e.preventDefault()
    const files = imageItems.map((item) => {
      const blob = item.getAsFile()!
      const ext = item.type.split('/')[1] || 'png'
      return new File([blob], `screenshot-${Date.now()}.${ext}`, { type: item.type })
    })
    for (const f of files) uploadFile(f)
  }

  return { fileInputRef, handleFileChange, handlePaste, insertAtCursor, uploadingCount, uploadError }
}
